import type { ReactNode } from 'react';

/**
 * 层级页签条（原型 v0.22 第 350–359 行 `#topTabs`，C-168 增「运营统计」一项）。
 *
 * | # | 页签 | 落位 | 数据 |
 * |---|---|---|---|
 * | 1 | 总览 | 现有渲染区（企业 / 部门 / 岗位站） | **真**：树、首屏三数、卡数 |
 * | 2 | 任务跟踪 | `StubView` | 原型即占位 |
 * | 3 | 审批中心 | `StubView` | 同上 |
 * | 4 | 日常审计 | `StubView` | 同上 |
 * | 5 | 承诺与结算 | `StubView` | 同上 |
 * | 6 | 数据与接入 | `StubView` + **原始记录池入口**（那一页是真的） | 混 |
 * | 7 | 建设进度 | `StubView` | 原型即占位 |
 * | 8 | 运营统计 | `StatsPage` | **真**（六张统计表建成，本轮 12 期为模拟数据且已在库内标注） |
 * | 9 | ATC 设置 | `AssetLedger` | **真**（达成率一列除外） |
 * | 10 | ATC 任务卡 | 现有 `AtcDetail` | **真** |
 *
 * ## 第 8 个页签是 C-168 加的，原型 v0.22 里没有
 *
 * 原型只有九项。运营统计是**新增的一项**，不是搬漏的一项——
 * 故这里只在数组里插一项、在 LABEL 里加一行，**其余各项一字不改、次序不动**。
 *
 * ## 最后一个页签（ATC 任务卡）跟着「选了哪张卡」出现，不常驻
 *
 * 原型 `atcTabBtn` 的 `display` 由 `selected` 决定（925–926 行）。照搬：
 * **没选卡时它不存在**，而不是存在但点了跳空页——后者会让人以为功能坏了。
 *
 * ## 页签状态放地址栏（`?tab=`）
 *
 * 与 `?node=` / `?view=` 同一条理由（`AppShell` 里已写）：刷新不丢、能收藏、
 * 能把「看这个」发给同事。**不放组件状态**。
 */
export const TAB_KEYS = [
  'home',
  'track',
  'approve',
  'audit',
  'commit',
  'data',
  'build',
  // 运营统计（C-168 A5）。**加在建设进度与 ATC 设置之间**，位置即语义：
  // 建设进度看「建到哪儿了」，运营统计看「建成之后跑出了什么」，ATC 设置管资产台账。
  'stats',
  'setup',
  'atc',
] as const;

export type TabKey = (typeof TAB_KEYS)[number];

const LABEL: Record<TabKey, string> = {
  home: '总览',
  track: '任务跟踪',
  approve: '审批中心',
  audit: '日常审计',
  commit: '承诺与结算',
  data: '数据与接入',
  build: '建设进度',
  stats: '运营统计',
  setup: 'ATC 设置',
  atc: 'ATC 任务卡',
};

/** 地址栏里的 `?tab=` 认不认。认不出就当总览——不报错，也不显示空白页。 */
export function tabKeyOf(raw: string | null): TabKey {
  return TAB_KEYS.includes(raw as TabKey) ? (raw as TabKey) : 'home';
}

export function TopTabs({
  current,
  showAtc,
  onPick,
}: {
  current: TabKey;
  /** 选中了一张卡才显示最后那个「ATC 任务卡」页签（原型 925–926 行）。 */
  showAtc: boolean;
  onPick: (tab: TabKey) => void;
}): ReactNode {
  return (
    <div className="pv22">
      <div className="tabs" role="tablist" aria-label="顶部页签">
        {TAB_KEYS.filter((key) => key !== 'atc' || showAtc).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={current === key}
            className={current === key ? 'on' : ''}
            onClick={() => onPick(key)}
          >
            {LABEL[key]}
          </button>
        ))}
      </div>
    </div>
  );
}
